import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";
import type {
  ProgramSource,
  ProgramSourceDisabled,
  ProgramSourceOverride,
  ProgramSourceStatus,
} from "@/services/adminProgramNews";
import { describeSourceReason } from "./sourceReason";
import { formatSchoolName, splitTeamId } from "./teamLabel";
import Term from "./Term";
import { sourceStatusTerm } from "./glossary";
import SourceUrlCell from "./SourceUrlCell";
import SourceDisableAction from "./SourceDisableAction";
import { isPlaceholderUrl } from "./sourceUrl";

interface SourceRowProps {
  source: ProgramSource;
  /** False outside QA: no action controls are rendered. */
  writable: boolean;
  /** Resolves when the override is saved; rejects on failure (the parent toasts). */
  onSetOverride: (teamId: string, url: string, reason: string) => Promise<unknown>;
  onDisable: (teamId: string, reason: string) => Promise<unknown>;
  onEnable: (teamId: string) => Promise<unknown>;
}

export const OVERRIDE_HELP =
  "Paste the athletics site's home page or the golf news page. We'll look for a SIDEARM or WMT feed.";

const STATUS_VARIANT: Record<
  ProgramSourceStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  verified: "default",
  queued: "secondary",
  failed: "destructive",
  disabled: "outline",
  override_stale: "destructive",
};

function formatTime(at: string | null | undefined): string {
  if (!at) return "never";
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? at : date.toLocaleString();
}

function overrideTitle(override: ProgramSourceOverride): string {
  return [override.setBy || "unknown", formatTime(override.setAt), override.reason]
    .filter(Boolean)
    .join(" · ");
}

/** Tooltip for a disabled row: why, who, when. Undefined when the API did not report it. */
function disabledTitle(source: ProgramSource): string | undefined {
  if (source.status !== "disabled") return undefined;
  const info: ProgramSourceDisabled | null | undefined = source.disabled;
  if (!info) return undefined;
  return [info.reason, info.setBy || "unknown", formatTime(info.setAt)].filter(Boolean).join(" · ");
}

/** Best URL to start an override from; never the "unknown" placeholder. */
function defaultOverrideUrl(source: ProgramSource): string {
  const candidates = [source.override?.url, source.verificationUrl, source.url];
  return candidates.find(value => !isPlaceholderUrl(value)) ?? "";
}

const SourceRow = ({ source, writable, onSetOverride, onDisable, onEnable }: SourceRowProps) => {
  const [editing, setEditing] = useState(false);
  const [draftUrl, setDraftUrl] = useState("");
  const [draftReason, setDraftReason] = useState("");
  const [pending, setPending] = useState(false);

  const { slug, gender } = splitTeamId(source.teamId);
  const attempt = source.state?.lastAttempt ?? null;
  const reason = describeSourceReason(source);
  const showSuggestion = source.status === "override_stale" && !!source.suggestedUrl;

  function openEditor(prefill?: string) {
    setDraftUrl(prefill ?? defaultOverrideUrl(source));
    setDraftReason("");
    setEditing(true);
  }

  function closeEditor() {
    setEditing(false);
    setDraftUrl("");
    setDraftReason("");
  }

  const canSave = !pending && draftUrl.trim().length > 0 && draftReason.trim().length > 0;

  async function handleSave() {
    const url = draftUrl.trim();
    const reasonText = draftReason.trim();
    if (!url || !reasonText) return;
    setPending(true);
    try {
      await onSetOverride(source.teamId, url, reasonText);
      closeEditor();
    } catch {
      // Parent surfaces the error; keep the form open so the entry can be corrected.
    } finally {
      setPending(false);
    }
  }

  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1">
          <span className="font-medium" title={source.teamId}>
            {formatSchoolName(slug)}
          </span>
          {source.onList ? (
            <Badge variant="secondary" className="text-[11px] font-normal">
              <Term term="on_list">On a list</Term>
            </Badge>
          ) : null}
        </div>
        <div className="text-xs text-muted-foreground">{gender}</div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1">
          <Badge variant={STATUS_VARIANT[source.status] ?? "outline"}>
            <Term term={sourceStatusTerm(source.status)} detail={disabledTitle(source)}>
              {source.status}
            </Term>
          </Badge>
          {source.override ? (
            <Badge variant="outline" title={overrideTitle(source.override)}>
              override
            </Badge>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="text-sm">{source.cmsFamily || "-"}</TableCell>
      <TableCell className="text-sm">
        <SourceUrlCell
          url={source.url}
          suggestedUrl={source.suggestedUrl}
          showSuggestion={showSuggestion}
          onUseSuggested={
            writable && !editing && showSuggestion
              ? () => openEditor(source.suggestedUrl as string)
              : null
          }
        />
      </TableCell>
      <TableCell className="text-sm">
        {source.state ? (
          <>
            <div>{formatTime(attempt?.at ?? source.state.lastFetchedAt)}</div>
            {attempt?.ok ? (
              <div className="text-xs text-muted-foreground">{attempt.itemCount ?? 0} items</div>
            ) : reason ? (
              <div
                className="text-xs text-destructive break-words"
                title={attempt?.error ?? undefined}
              >
                {reason}
              </div>
            ) : null}
          </>
        ) : (
          <span className="text-muted-foreground">never fetched</span>
        )}
      </TableCell>
      <TableCell>
        {!writable ? null : editing ? (
          <div className="flex flex-col gap-2 min-w-[20rem]">
            <Input
              value={draftUrl}
              onChange={e => setDraftUrl(e.target.value)}
              disabled={pending}
              placeholder="https://gofrogs.com/"
              aria-label="Athletics site URL"
            />
            <p className="text-xs text-muted-foreground">{OVERRIDE_HELP}</p>
            <Input
              value={draftReason}
              onChange={e => setDraftReason(e.target.value)}
              disabled={pending}
              required
              placeholder="Reason (required)"
              aria-label="Reason"
            />
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSave} disabled={!canSave}>
                {pending ? (
                  <>
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save"
                )}
              </Button>
              <Button size="sm" variant="ghost" onClick={closeEditor} disabled={pending}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-2">
            <Button size="sm" variant="outline" onClick={() => openEditor()}>
              Set override
            </Button>
            <SourceDisableAction source={source} onDisable={onDisable} onEnable={onEnable} />
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

export default SourceRow;
